import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { signOut as gmailSignOut } from './googleAuth';
import { markDeviceSignIn, clearDeviceSignIn, deviceSessionExpired, DEVICE_DAYS } from './lib/pwa';
import { clearOffline } from './lib/offline';

// CRM-LEADS-2026-10-16 §4: signed in for 30 days on a device that has signed in; after that, sign in again.
const store = () => { try { return window.localStorage; } catch { return null; } };

export default function AuthGate({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const [expiredNote, setExpiredNote] = useState('');
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session && deviceSessionExpired(store())) {
        clearDeviceSignIn(store());
        await supabase.auth.signOut();
        setExpiredNote(`For security, sign in again: it has been ${DEVICE_DAYS} days on this device.`);
        setSession(null);
      } else {
        if (session) markDeviceSignIn(store());
        setSession(session);
      }
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') markDeviceSignIn(store());
      if (event === 'SIGNED_OUT') { clearDeviceSignIn(store()); if (store()) clearOffline(store()); }   // §6 (10-22): the saved lists and queue go too
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0f172a' }}>
      <div style={{ color: '#94a3b8', fontSize: '18px' }}>Loading...</div>
    </div>
  );

  if (!session) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0f172a' }}>
      <div style={{ background: '#1e293b', borderRadius: '12px', padding: '40px', width: '100%', maxWidth: '400px', boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f8fafc', marginBottom: '8px' }}>TherapyLink CRM</div>
          <div style={{ color: '#94a3b8', fontSize: '14px' }}>Sign in to continue</div>
        </div>
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', color: '#94a3b8', fontSize: '13px', marginBottom: '6px' }}>Email</label>
            <input type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} required
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: '14px', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', color: '#94a3b8', fontSize: '13px', marginBottom: '6px' }}>Password</label>
            <input type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: '14px', boxSizing: 'border-box' }} />
          </div>
          {expiredNote && !error && <div data-testid="auth-expired" style={{ color: '#fbbf24', fontSize: '13px', marginBottom: '16px' }}>{expiredNote}</div>}
          {error && <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>{error}</div>}
          <button type="submit" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>Sign In</button>
        </form>
      </div>
    </div>
  );

  return (
    <>
      {children}
      <div style={{ position: 'fixed', top: 10, right: 12, zIndex: 9999 }}><SignOutButton /></div>
    </>
  );
}

export function SignOutButton() {
  // SECURITY-AGENTS-2026-10-14: signing out also drops the Gmail token (it lived on in localStorage on a shared machine).
  // NIGHT-DIRECTSTAFF-2026-10-22 §6: and the saved offline lists and the unsent queue
  const out = () => { try { gmailSignOut(); } catch (_) { /* no Gmail session */ } if (store()) clearOffline(store()); return supabase.auth.signOut(); };
  return <button onClick={out} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #334155', background: 'transparent', color: '#94a3b8', fontSize: '12px', cursor: 'pointer' }}>Sign Out</button>;
        }
